import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Paperclip, Send, X } from "lucide-react";

const DEFAULT_API_BASE_URL = "https://candi.vdpl.co/api/index.php/";

const splitRecipients = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeRecipients = (value) => Array.from(new Set(splitRecipients(value))).join(", ");

const toSendEmailEndpoint = (apiBaseUrl) => {
  const base = String(apiBaseUrl || DEFAULT_API_BASE_URL).trim();
  const normalizedBase = base.endsWith("/") ? base : `${base}/`;
  return `${normalizedBase}send_to_email`;
};

const formatFileSize = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const extractTextFromHtml = (value) => {
  const html = String(value || "");
  if (!html) return "";
  if (typeof document === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  const root = document.createElement("div");
  root.innerHTML = html;
  return String(root.textContent || root.innerText || "").replace(/\u00a0/g, " ").trim();
};

const isHtmlBodyEmpty = (value) => {
  const html = String(value || "").trim();
  if (!html) return true;
  const plainText = extractTextFromHtml(html);
  if (plainText) return false;
  return !/<(img|table|a|br|hr|ul|ol|li|p|div|span|strong|em|h[1-6]|blockquote|pre|code|video|audio)\b/i.test(html);
};

const RDEmailComposeDialog = ({
  isOpen = false,
  onClose,
  initialTo = "",
  apiBaseUrl = DEFAULT_API_BASE_URL,
  onSent,
}) => {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyMode, setBodyMode] = useState("rich");
  const [attachments, setAttachments] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const bodyHtmlRef = useRef("");

  const endpoint = useMemo(() => toSendEmailEndpoint(apiBaseUrl), [apiBaseUrl]);
  const hasBodyContent = useMemo(() => !isHtmlBodyEmpty(bodyHtml), [bodyHtml]);
  useEffect(() => {
    bodyHtmlRef.current = bodyHtml;
  }, [bodyHtml]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setTo(normalizeRecipients(initialTo));
    setCc("");
    setBcc("");
    setSubject("");
    setBodyHtml("");
    setBodyMode("rich");
    setAttachments([]);
    setShowCc(false);
    setShowBcc(false);
    setError("");
    bodyHtmlRef.current = "";
    if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = "";

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [initialTo, isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || bodyMode !== "rich" || !bodyEditorRef.current) return;
    bodyEditorRef.current.innerHTML = bodyHtmlRef.current || "";
  }, [bodyMode, isOpen]);

  const handleAddAttachment = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachments((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const removeAttachment = (indexToRemove) => {
    setAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedTo = normalizeRecipients(to);
    const normalizedCc = normalizeRecipients(cc);
    const normalizedBcc = normalizeRecipients(bcc);
    const normalizedSubject = String(subject || "").trim();
    const normalizedBody = String(
      bodyMode === "rich" ? bodyEditorRef.current?.innerHTML || bodyHtml : bodyHtml,
    );

    if (!normalizedTo) {
      setError("To is required.");
      return;
    }
    if (!normalizedSubject) {
      setError("Subject is required.");
      return;
    }
    if (isHtmlBodyEmpty(normalizedBody)) {
      setError("Body is required.");
      return;
    }

    setIsSending(true);
    setError("");
    try {
      const payload = new FormData();
      payload.append("to", normalizedTo);
      payload.append("cc", normalizedCc);
      payload.append("bcc", normalizedBcc);
      payload.append("subject", normalizedSubject);
      payload.append("body", normalizedBody);
      attachments.forEach((file) => payload.append("attachments[]", file, file.name));

      const response = await fetch(endpoint, { method: "POST", body: payload });
      const raw = await response.text();
      let result = null;
      try {
        result = raw ? JSON.parse(raw) : null;
      } catch {
        result = null;
      }

      const isError =
        !response.ok ||
        result?.success === false ||
        String(result?.status || "").toLowerCase() === "error";
      if (isError) {
        throw new Error(
          result?.message ||
            result?.error ||
            `Unable to send email (HTTP ${response.status}).`,
        );
      }

      onSent?.({
        to: normalizedTo,
        cc: normalizedCc,
        bcc: normalizedBcc,
        subject: normalizedSubject,
        body: normalizedBody,
        attachmentsCount: attachments.length,
        result,
      });
      onClose?.();
    } catch (sendError) {
      setError(sendError?.message || "Unable to send email.");
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-md p-0 sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full sm:max-w-4xl h-[92vh] sm:h-[82vh] rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/10 overflow-hidden scale-in flex flex-col"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 bg-slate-50">
          <h3 className="text-sm font-bold text-slate-800">Compose mail</h3>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="w-8 h-8 rounded-xl text-slate-500 hover:bg-slate-200 transition-colors flex items-center justify-center"
            aria-label="Close compose dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="border-b border-slate-200 px-4 py-2 flex items-center gap-3">
            <label className="text-xs font-semibold text-slate-500 w-10">To</label>
            <input
              type="text"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              placeholder="name@example.com"
              className="flex-1 text-sm border-0 focus:outline-none bg-transparent text-slate-700 placeholder:text-slate-300"
            />
            <button
              type="button"
              onClick={() => setShowCc((prev) => !prev)}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
            >
              Cc
            </button>
            <button
              type="button"
              onClick={() => setShowBcc((prev) => !prev)}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
            >
              Bcc
            </button>
          </div>

          {showCc && (
            <div className="border-b border-slate-200 px-4 py-2 flex items-center gap-3">
              <label className="text-xs font-semibold text-slate-500 w-10">Cc</label>
              <input
                type="text"
                value={cc}
                onChange={(event) => setCc(event.target.value)}
                placeholder="cc@example.com"
                className="flex-1 text-sm border-0 focus:outline-none bg-transparent text-slate-700 placeholder:text-slate-300"
              />
            </div>
          )}

          {showBcc && (
            <div className="border-b border-slate-200 px-4 py-2 flex items-center gap-3">
              <label className="text-xs font-semibold text-slate-500 w-10">Bcc</label>
              <input
                type="text"
                value={bcc}
                onChange={(event) => setBcc(event.target.value)}
                placeholder="bcc@example.com"
                className="flex-1 text-sm border-0 focus:outline-none bg-transparent text-slate-700 placeholder:text-slate-300"
              />
            </div>
          )}

          <div className="border-b border-slate-200 px-4 py-2 flex items-center gap-3">
            <label className="text-xs font-semibold text-slate-500 w-10">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Email subject"
              className="flex-1 text-sm border-0 focus:outline-none bg-transparent text-slate-700 placeholder:text-slate-300"
            />
          </div>

          <div className="border-b border-slate-200 px-4 py-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-500">
              Body is sent as HTML in payload.
            </span>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button
                type="button"
                onClick={() => setBodyMode("rich")}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                  bodyMode === "rich"
                    ? "bg-indigo-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Rich
              </button>
              <button
                type="button"
                onClick={() => {
                  if (bodyEditorRef.current) setBodyHtml(bodyEditorRef.current.innerHTML || "");
                  setBodyMode("html");
                }}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                  bodyMode === "html"
                    ? "bg-indigo-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                HTML
              </button>
            </div>
          </div>

          {bodyMode === "html" ? (
            <textarea
              value={bodyHtml}
              onChange={(event) => setBodyHtml(event.target.value)}
              placeholder="<p>Paste your HTML here...</p>"
              className="flex-1 min-h-[220px] resize-none border-0 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-300 focus:outline-none font-mono"
            />
          ) : (
            <div className="relative flex-1 min-h-[220px]">
              <div
                ref={bodyEditorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={(event) => setBodyHtml(event.currentTarget.innerHTML || "")}
                className="h-full min-h-[220px] overflow-y-auto border-0 px-4 py-3 text-sm text-slate-700 focus:outline-none"
              />
              {!hasBodyContent && (
                <span className="pointer-events-none absolute left-4 top-3 text-sm text-slate-300">
                  Write your message or paste rich HTML content...
                </span>
              )}
            </div>
          )}

          <div className="border-t border-slate-200 px-4 py-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleAddAttachment}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Paperclip className="h-3.5 w-3.5" />
              Add attachment
            </button>
            {attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {attachments.map((file, index) => (
                  <span
                    key={`${file.name}-${index}`}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600"
                  >
                    <span>{file.name}</span>
                    <span className="text-slate-400">({formatFileSize(file.size)})</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="text-slate-400 hover:text-rose-600 transition-colors"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-3 bg-slate-50">
            <div className="min-h-[20px]">
              {error && <p className="text-xs font-semibold text-rose-600">{error}</p>}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onClose?.()}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                disabled={isSending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSending}
                className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default RDEmailComposeDialog;
